import {
  Button,
  Checkbox,
  Input,
  Label,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@/components/ui";
import { DataTableVisibilityDropdown } from "@/components/ui/shadcn/DataTable/DataTableVisibilityDropdown";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { dashboardUnassignedActionsQueryOptions } from "@/lib/data/react-query/matters/dashboard-tables";
import type {
  DashboardUnassignedActionRow,
  DashboardUnassignedActionsFilters,
  MatterDashboardTable,
} from "@/lib/types/MatterDashboardTable";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { useEffect, useId, useState } from "react";

import { DashboardClaimActionButton } from "./DashboardClaimActionButton";
import { DashboardMatterDataCell } from "./DashboardMatterDataCell";
import { DashboardMoreFilters } from "./DashboardMoreFilters";

const DEFAULT_PAGE_SIZE = 25;
const MAX_SAVED_VIEWS = 50;
const COLUMN_VISIBILITY_STORAGE_PREFIX = "lawworks:dashboard-table-columns:";

function readStoredColumnKeys(storageKey: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) &&
      parsed.every((key) => typeof key === "string")
      ? parsed
      : null;
  } catch {
    return null;
  }
}

type SortBy = NonNullable<DashboardUnassignedActionsFilters["sortBy"]>;
type SortOrder = NonNullable<DashboardUnassignedActionsFilters["sortOrder"]>;
type TableView = {
  search: string;
  myTeams: boolean;
  massTortId: string | undefined;
  sortBy: SortBy;
  sortOrder: SortOrder;
  page: number;
  pageSize: number;
  visibleColumnKeys: string[] | null;
};

const savedViews = new Map<string, TableView>();

function rememberView(key: string, view: TableView) {
  savedViews.delete(key);
  savedViews.set(key, view);
  if (savedViews.size > MAX_SAVED_VIEWS) {
    const oldestKey = savedViews.keys().next().value;
    if (oldestKey) savedViews.delete(oldestKey);
  }
}

function formatPhaseStartedAt(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function SortableHead({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
  className,
  primary = false,
}: {
  label: string;
  field: SortBy;
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSort: (field: SortBy) => void;
  className?: string;
  primary?: boolean;
}) {
  const selected = sortBy === field;
  return (
    <TableHead
      className={className}
      aria-sort={
        selected ? (sortOrder === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className={`${primary ? "text-foreground" : "text-muted-foreground"} hover:bg-muted/50 inline-flex max-w-full cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 text-left`}
        aria-label={`Sort by ${label.toLowerCase()}`}
        onClick={() => {
          onSort(field);
        }}
      >
        <span>{label}</span>
        {selected &&
          (sortOrder === "asc" ? (
            <ArrowUp className="size-3.5 shrink-0" aria-hidden />
          ) : (
            <ArrowDown className="size-3.5 shrink-0" aria-hidden />
          ))}
      </button>
    </TableHead>
  );
}

export function UnassignedActionsTable({
  table,
  firmId,
  userId,
  ready,
  afterSequence,
}: {
  table: MatterDashboardTable;
  firmId: string;
  userId: string;
  ready: boolean;
  afterSequence: number;
}) {
  const viewKey = JSON.stringify([firmId, userId, table.id]);
  const columnVisibilityStorageKey = `${COLUMN_VISIBILITY_STORAGE_PREFIX}${viewKey}`;
  const [initialView] = useState<TableView>(
    () =>
      savedViews.get(viewKey) ?? {
        search: "",
        myTeams: false,
        massTortId: undefined,
        sortBy: "phaseStartedAt",
        sortOrder: "asc",
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        visibleColumnKeys: readStoredColumnKeys(columnVisibilityStorageKey),
      },
  );
  const [search, setSearch] = useState(initialView.search);
  const [myTeams, setMyTeams] = useState(initialView.myTeams);
  const [massTortId, setMassTortId] = useState(initialView.massTortId);
  const [sortBy, setSortBy] = useState<SortBy>(initialView.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialView.sortOrder);
  const [page, setPage] = useState(initialView.page);
  const [pageSize, setPageSize] = useState(initialView.pageSize);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[] | null>(
    initialView.visibleColumnKeys,
  );
  useEffect(() => {
    rememberView(viewKey, {
      search,
      myTeams,
      massTortId,
      sortBy,
      sortOrder,
      page,
      pageSize,
      visibleColumnKeys,
    });
  }, [
    viewKey,
    search,
    myTeams,
    massTortId,
    sortBy,
    sortOrder,
    page,
    pageSize,
    visibleColumnKeys,
  ]);
  useEffect(() => {
    try {
      if (visibleColumnKeys === null) {
        window.localStorage.removeItem(columnVisibilityStorageKey);
      } else {
        window.localStorage.setItem(
          columnVisibilityStorageKey,
          JSON.stringify(visibleColumnKeys),
        );
      }
    } catch {
      // Keep the current view usable when storage is unavailable.
    }
  }, [columnVisibilityStorageKey, visibleColumnKeys]);
  const myTeamsId = useId();
  const debouncedSearch = useDebouncedValue(search, 300);
  const filters: DashboardUnassignedActionsFilters = {
    q: debouncedSearch.trim() || undefined,
    myTeams,
    massTortId,
    sortBy,
    sortOrder,
    pageNumber: page,
    pageSize,
    afterSequence,
  };
  const { data, isPending, isError, isFetching, refetch } = useQuery({
    ...dashboardUnassignedActionsQueryOptions(
      firmId,
      userId,
      table.id,
      filters,
    ),
    enabled: ready,
  });
  const rows: DashboardUnassignedActionRow[] = data?.data.rows ?? [];
  const dataColumnMetadata = new Map(
    (data?.data.dataColumns ?? []).map(
      (column) => [column.key, column] as const,
    ),
  );
  const dataColumns = (data?.data.columnOrder ?? [...dataColumnMetadata.keys()])
    .map((key) => dataColumnMetadata.get(key))
    .filter((column): column is NonNullable<typeof column> => !!column);
  const visibleDataColumns = dataColumns.filter((column) =>
    visibleColumnKeys ? visibleColumnKeys.includes(column.key) : true,
  );
  const total = data?.pagination?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstAction = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastAction = Math.min(page * pageSize, total);

  useEffect(() => {
    if (!isPending && page > pageCount) setPage(pageCount);
  }, [isPending, page, pageCount]);

  const updateSort = (field: SortBy) => {
    if (sortBy === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  return (
    <section
      className="bg-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      aria-label={table.title}
    >
      <div className="shrink-0 px-4 py-3">
        <h3 className="text-base font-semibold">{table.title}</h3>
      </div>
      <div className="bg-muted/30 flex shrink-0 flex-wrap items-center justify-between gap-3 border-y px-4 py-3">
        <div className="relative w-full min-w-48 sm:w-80">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            aria-label={`Search actions in ${table.title}`}
            placeholder="Search actions"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="bg-card h-10 pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DashboardMoreFilters
            massTortId={massTortId}
            onMassTortChange={(value) => {
              setMassTortId(value);
              setPage(1);
            }}
          />
          <div className="flex items-center gap-2 px-2">
            <Checkbox
              id={myTeamsId}
              checked={myTeams}
              onCheckedChange={(checked) => {
                setMyTeams(checked === true);
                setPage(1);
              }}
            />
            <Label htmlFor={myTeamsId} className="cursor-pointer text-sm">
              Assigned to me or my teams
            </Label>
          </div>
          {dataColumns.length > 0 && (
            <DataTableVisibilityDropdown
              columns={dataColumns.map((column) => ({
                id: column.key,
                label: column.title,
                isVisible: visibleColumnKeys
                  ? visibleColumnKeys.includes(column.key)
                  : true,
              }))}
              onVisibilityChange={(columnIds, visible) => {
                const changedIds = new Set(columnIds);
                const next = visible
                  ? Array.from(
                      new Set([
                        ...visibleDataColumns.map((column) => column.key),
                        ...columnIds,
                      ]),
                    )
                  : visibleDataColumns
                      .filter((column) => !changedIds.has(column.key))
                      .map((column) => column.key);
                setVisibleColumnKeys(
                  visible && next.length === dataColumns.length ? null : next,
                );
              }}
            />
          )}
        </div>
      </div>
      <Table
        containerClassName="min-h-0 min-w-0 flex-1 overscroll-contain"
        className="min-w-full text-sm"
      >
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <SortableHead
              label="Action"
              field="actionTitle"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={updateSort}
              primary
              className="bg-muted after:bg-border sticky top-0 left-0 z-30 w-52 min-w-52 px-3 py-2 text-xs font-medium after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px"
            />
            <SortableHead
              label="Matter"
              field="matterTitle"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={updateSort}
              className="bg-muted sticky top-0 z-20 min-w-52 px-3 py-2 text-xs font-medium"
            />
            <TableHead className="bg-muted text-muted-foreground sticky top-0 z-20 min-w-40 px-3 py-2 text-xs font-medium">
              Phase
            </TableHead>
            <SortableHead
              label="Phase started"
              field="phaseStartedAt"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={updateSort}
              className="bg-muted sticky top-0 z-20 min-w-40 px-3 py-2 text-xs font-medium"
            />
            {visibleDataColumns.map((column) => (
              <TableHead
                key={column.key}
                className="bg-muted text-muted-foreground sticky top-0 z-20 min-w-52 px-3 py-2 text-xs font-medium"
              >
                {column.title}
              </TableHead>
            ))}
            <TableHead className="bg-muted text-muted-foreground after:bg-border sticky top-0 right-0 z-30 w-20 max-w-20 min-w-20 px-2 py-2 text-right text-xs font-medium after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-px">
              Claim
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isPending || (isFetching && !data) ? (
            <TableRow>
              <TableCell
                colSpan={visibleDataColumns.length + 5}
                className="h-28 text-center"
              >
                <span role="status" className="inline-flex items-center gap-2">
                  <LoadingSpinner />
                  Loading actions…
                </span>
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell
                colSpan={visibleDataColumns.length + 5}
                className="px-4 py-10 text-center text-sm"
              >
                Unable to load actions.{" "}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void refetch()}
                >
                  Try again
                </Button>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleDataColumns.length + 5}
                className="px-4 py-10 text-center text-sm"
              >
                No unassigned actions found.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.phaseActionId}>
                <TableCell className="bg-card after:bg-border sticky left-0 z-10 w-52 min-w-52 px-3 py-3 align-top font-medium after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px">
                  {row.title}
                </TableCell>
                <TableCell className="text-muted-foreground min-w-52 px-3 py-3 align-top">
                  <Link
                    to="/matters/$matterId"
                    params={{ matterId: row.matterId }}
                    className="text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {row.matterTitle?.trim() || "Untitled matter"}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground min-w-40 px-3 py-3 align-top">
                  {row.phaseTitle}
                </TableCell>
                <TableCell className="text-muted-foreground min-w-40 px-3 py-3 align-top">
                  {formatPhaseStartedAt(row.phaseStartedAt)}
                </TableCell>
                {visibleDataColumns.map((column) => (
                  <TableCell
                    key={`${row.phaseActionId}:${column.key}`}
                    className="min-w-52 px-3 py-3 align-top"
                  >
                    <DashboardMatterDataCell
                      cell={row.dataCells?.[column.key]}
                      matterId={row.matterId}
                    />
                  </TableCell>
                ))}
                <TableCell className="bg-card after:bg-border sticky right-0 z-10 w-20 max-w-20 min-w-20 px-2 py-3 text-right align-top after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-px">
                  <DashboardClaimActionButton
                    action={row}
                    matterId={row.matterId}
                    userId={userId}
                    firmId={firmId}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {!isPending && !isError && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-2.5">
          <Text variant="subHeading">
            {total === 0
              ? "No actions"
              : total === 1
                ? "1 action"
                : `Showing ${firstAction} to ${lastAction} of ${total} actions`}
          </Text>
          {pageCount > 1 && (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center gap-2">
                <Text
                  as="span"
                  variant="subHeading"
                  className="whitespace-nowrap"
                >
                  Rows per page:
                </Text>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    className="h-8 w-[80px] shrink-0"
                    aria-label="Rows per page"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 25, 50, 100].map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((value) => value - 1);
                  }}
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft />
                </Button>
                <Text as="span" variant="body" className="whitespace-nowrap">
                  Page {page} of {pageCount}
                </Text>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  disabled={page >= pageCount}
                  onClick={() => {
                    setPage((value) => value + 1);
                  }}
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
